import { useTranslation } from "react-i18next";
import { convertDoubleToStringPTBR } from "../../../../../utils/ConverterUtils";
import HintTooltip from "../../../Common/HintTooltip";
import { WEEK_DAYS } from "../../../../../utils/WorkDayUtils";
import { getSubscriptionSituation } from "../subscriptionSituation";
import { formatDate } from "../../../../../utils/DateUtils";
import PlanAttentionNotices from "./PlanAttentionNotices";
import "./PlanDetail.css";

// Cor do quadro de situação pela tonalidade da regra comum, a mesma do selo na listagem de
// assinaturas: ativa em verde, pendente em amarelo, em atraso em vermelho, cancelada neutra.
const SITUATION_VARIANT = {
    active: 'success',
    pending: 'warning',
    overdue: 'danger',
    inactive: 'neutral',
};

// Ícone de cada situação de getSubscriptionSituation.
const SITUATION_ICON = {
    up_to_date: 'fas fa-check-circle',
    overdue: 'fas fa-exclamation-circle',
    awaiting_payment: 'fas fa-hourglass-half',
    payment_link_expired: 'fas fa-calendar-times',
    cancelled: 'fas fa-ban',
    package_ended: 'fas fa-calendar-check',
};

const fmt = (dateStr) => {
    if (!dateStr) return '-';
    try { return new Date(dateStr).toLocaleDateString('pt-BR'); } catch { return dateStr; }
};

const PlanDetail = ({ entity }) => {
    const { t } = useTranslation();

    if (!entity || !entity.plan) return null;

    const { plan, lastChargeDueDate, lastChargePaymentDate,
            overdueChargeCount, planBenefitUsage, chargedAtStore, storePackageValidUntil } = entity;

    // Mesma situação da tela de Assinaturas. Antes vinha do "último status" gravado pela
    // integração, que segue o provedor e não a data de pagamento: o cliente em dia aparecia
    // "Ativo" em Assinaturas e "Aguardando pagamento" aqui.
    const situation = getSubscriptionSituation(entity);
    const statusLabel = t(situation.key);
    const statusHint = t(`subscription_situation_hint.${situation.key}`);
    const statusVariant = SITUATION_VARIANT[situation.tone];
    const statusIcon = SITUATION_ICON[situation.key];

    const products = plan.planProducts || [];

    return (
        <div className="plan-detail">

            {/* Header do plano */}
            <div className="plan-detail__header">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div>
                        <div className="plan-detail__header-label">
                            {t('plan')}
                        </div>
                        <h4 className="mb-0 mt-1 plan-detail__header-name">
                            {plan.name}
                            {/* Código do plano, o mesmo da primeira coluna da tela de Planos: o nome pode
                                se repetir entre um plano desativado e um novo, e o código diz qual é. */}
                            {plan.id && (
                                <span className="plan-detail__header-code" title={t('plan_code')}>#{plan.id}</span>
                            )}
                        </h4>
                        {plan.periodicity && (
                            <span className="plan-detail__header-meta">
                                <i className="fas fa-sync-alt me-1"></i>
                                {/* As chaves de periodicidade seguem o enum do backend em minúsculo
                                    (monthly, bimonthly, quarterly, semi_annual, yearly). O mapa fixo
                                    anterior tinha valores que não existem no enum, então plano
                                    trimestral ou semestral aparecia como "QUARTERLY"/"SEMI_ANNUAL". */}
                                {t(String(plan.periodicity).toLowerCase(), { defaultValue: plan.periodicity })}
                            </span>
                        )}
                    </div>
                    <div className="text-end">
                        <div className="plan-detail__header-label">{t('price')}</div>
                        <div className="plan-detail__header-price">
                            {t('currency_symbol')} {convertDoubleToStringPTBR(plan.price || 0)}
                        </div>
                        {plan.qtyTransactions > 0 && (
                            <div className="plan-detail__header-label">
                                {plan.qtyTransactions} {t('qty_transactions').toLowerCase()}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Plano sem serviços (nenhum desconto) e plano desativado para vendas novas. */}
            <PlanAttentionNotices plan={plan} />

            {/* Situação da assinatura */}
            <div className={`plan-detail__status plan-detail__status--${statusVariant}`}>
                <i className={`${statusIcon} fa-lg plan-detail__status-icon`}></i>
                <div>
                    <div className="plan-detail__status-label">
                        {t('status')}
                        <HintTooltip text={statusHint} />
                    </div>
                    <div className="plan-detail__status-value">
                        {statusLabel}
                    </div>
                </div>
                {overdueChargeCount > 0 && (
                    <span className="badge bg-danger ms-auto" style={{ fontSize: '0.8rem' }}>
                        <i className="fas fa-exclamation-triangle me-1"></i>
                        {overdueChargeCount} {overdueChargeCount === 1 ? t('overdue_invoice') : t('overdue_invoices')}
                    </span>
                )}
            </div>

            {/* Datas da última cobrança. No pacote da loja o vencimento é só o início do
                período pago; o que importa a quem atende é até quando ele vale. */}
            {(lastChargeDueDate || lastChargePaymentDate) && (
                <div className="row g-2 mb-3">
                    {chargedAtStore && storePackageValidUntil ? (
                        <div className="col-6">
                            <div className="plan-detail__meta-card">
                                <div className="plan-detail__meta-label">
                                    <i className="fas fa-calendar-check me-1"></i> {t('store_package_valid_until')}
                                </div>
                                <div className="plan-detail__meta-value">
                                    {formatDate(storePackageValidUntil)}
                                </div>
                            </div>
                        </div>
                    ) : lastChargeDueDate && (
                        <div className="col-6">
                            <div className="plan-detail__meta-card">
                                <div className="plan-detail__meta-label">
                                    <i className="fas fa-calendar-alt me-1"></i> {t('due_date')}
                                </div>
                                <div className="plan-detail__meta-value">
                                    {fmt(lastChargeDueDate)}
                                </div>
                            </div>
                        </div>
                    )}
                    {lastChargePaymentDate && (
                        <div className="col-6">
                            <div className="plan-detail__meta-card plan-detail__meta-card--paid">
                                <div className="plan-detail__meta-label">
                                    <i className="fas fa-check me-1"></i> {t('paid_at')}
                                </div>
                                <div className="plan-detail__meta-value plan-detail__meta-value--paid">
                                    {fmt(lastChargePaymentDate)}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Serviços e Descontos */}
            <div className="plan-detail__section plan-detail__days-section">
                <div className="plan-detail__section-title">
                    <i className="fas fa-calendar-week me-2 text-primary"></i>
                    Dias de benefício
                    <HintTooltip text="Dias da semana em que o plano oferece o desconto integral dos serviços incluídos." />
                </div>
                <div className="plan-detail__days">
                    {(plan.benefitDays || []).length === 0 ? (
                        <span className="plan-detail__days-all">Todos os dias</span>
                    ) : (
                        WEEK_DAYS
                            .filter(day => plan.benefitDays.includes(day.value))
                            .map(day => <span key={day.value} className="plan-detail__day">{day.label}</span>)
                    )}
                </div>
            </div>

            <div className="plan-detail__section">
                <div className="plan-detail__section-title">
                    <i className="fas fa-tags me-2 text-primary"></i>
                    {t('included_services')}
                </div>

                {products.length === 0 ? (
                    <div className="plan-detail__empty">
                        <i className="fas fa-info-circle me-1"></i>
                        {t('plan_without_services')}
                    </div>
                ) : (
                    <>
                        {products.length > 4 && (
                            <div className="plan-detail__section-hint">
                                <i className="fas fa-layer-group me-1"></i>
                                {t('plan_services_scroll_hint', { count: products.length })}
                            </div>
                        )}
                        <div className="plan-detail__services">
                            {products.map((item, index) => (
                                <div key={index} className="plan-detail__service">
                                    <div className="d-flex align-items-center gap-2">
                                        <div className="plan-detail__service-icon">
                                            <i className="fas fa-cut"></i>
                                        </div>
                                        <span className="plan-detail__service-name">
                                            {item.productName}
                                        </span>
                                    </div>
                                    {renderBenefitBadge(item.discountPercentage, t)}
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>

            {/* Cota de benefícios do plano */}
            {plan.maxAppointments > 0 && (
                <div className="plan-detail__quota">
                    <i className="fas fa-calendar-check fa-lg"></i>
                    <div>
                        <div className="plan-detail__quota-label">
                            {t('included_appointments')}
                            <HintTooltip text={t('message.plan_benefit_quota_hint')} />
                        </div>
                        <div className="plan-detail__quota-value">
                            {/* Quando o servidor manda a apuração do ciclo, mostra o que ainda
                                resta; sem ela, fica só o limite configurado no plano. */}
                            {planBenefitUsage && planBenefitUsage.remainingBenefits != null
                                ? t('plan_benefits_remaining', {
                                    remaining: planBenefitUsage.remainingBenefits,
                                    max: plan.maxAppointments
                                })
                                : t('plan_benefits_per_cycle', { max: plan.maxAppointments })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * Etiqueta do benefício de cada item do plano.
 *
 * Desconto zero NÃO é "incluso": o item continua sendo cobrado pelo valor cheio, tanto na
 * tela quanto na gravação da venda. Exibi-lo como "Incluso" fazia o operador esperar um item
 * gratuito que o sistema cobrava.
 */
const renderBenefitBadge = (discountPercentage, t) => {
    const discount = discountPercentage || 0;

    if (discount >= 100) {
        return (
            <span className="plan-detail__badge plan-detail__badge--free">
                <i className="fas fa-star me-1" style={{ fontSize: '0.65rem' }}></i>
                {t('included_in_plan')}
            </span>
        );
    }

    if (discount > 0) {
        return (
            <span className="plan-detail__badge plan-detail__badge--discount">
                <i className="fas fa-percent me-1" style={{ fontSize: '0.65rem' }}></i>
                {t('percent_off', { discount })}
            </span>
        );
    }

    return (
        <span className="plan-detail__badge plan-detail__badge--none">
            <i className="fas fa-minus me-1" style={{ fontSize: '0.65rem' }}></i>
            {t('no_discount')}
        </span>
    );
};

export default PlanDetail;
